import { useEffect, useCallback, useRef } from 'react';

import { useLocalStorage } from 'hooks/useStorage';
import { getPageType, PageTypes } from 'utils/pageTypes';
import { useLocation } from 'react-router-dom';
import { getWindowId } from 'utils/page';
import type { Dispatch, SetStateAction } from 'types';
import { clamp, isFiniteNumber, positiveOr } from 'utils/math';

type WindowRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

const MIN_WINDOW_EDGE = 1;
const MAX_COORDINATE_ABS = 10000;

export const sanitizeWindowRect = ({
  rect,
}: {
  rect: Nullable<Partial<WindowRect>>;
}): Nullable<WindowRect> => {
  if (rect == null) return null;

  const fallbackWidth = positiveOr(window.innerWidth, MIN_WINDOW_EDGE);
  const fallbackHeight = positiveOr(window.innerHeight, MIN_WINDOW_EDGE);

  const maxWidth = Math.max(MIN_WINDOW_EDGE, positiveOr(window.screen?.availWidth, fallbackWidth));
  const maxHeight = Math.max(
    MIN_WINDOW_EDGE,
    positiveOr(window.screen?.availHeight, fallbackHeight)
  );

  if (!isFiniteNumber(rect.width) || !isFiniteNumber(rect.height)) return null;

  const width = clamp(Math.round(rect.width), MIN_WINDOW_EDGE, maxWidth);
  const height = clamp(Math.round(rect.height), MIN_WINDOW_EDGE, maxHeight);

  const top = isFiniteNumber(rect.top)
    ? clamp(Math.round(rect.top), -MAX_COORDINATE_ABS, MAX_COORDINATE_ABS)
    : 0;
  const left = isFiniteNumber(rect.left)
    ? clamp(Math.round(rect.left), -MAX_COORDINATE_ABS, MAX_COORDINATE_ABS)
    : 0;

  return {
    top,
    left,
    width,
    height,
  };
};

export type WindowsState = Partial<
  Record<
    PageTypes,
    {
      [key: string]: WindowRect;
    }
  >
>;

export const sanitizeWindowsState = (
  state: Nullable<WindowsState>
): { nextState: WindowsState; didChange: boolean } => {
  if (state == null) return { nextState: {}, didChange: false };

  let didChange = false;
  const nextState = Object.entries(state).reduce<WindowsState>((acc, [pageType, windowsById]) => {
    if (windowsById == null) return acc;

    const sanitizedWindowsById = Object.entries(windowsById).reduce<Record<string, WindowRect>>(
      (nextWindowsById, [windowId, windowRect]) => {
        const sanitizedRect = sanitizeWindowRect({
          rect: windowRect,
        });
        if (sanitizedRect == null) {
          didChange = true;
          return nextWindowsById;
        }

        const r = windowRect as WindowRect;
        if (
          sanitizedRect.top !== r.top ||
          sanitizedRect.left !== r.left ||
          sanitizedRect.width !== r.width ||
          sanitizedRect.height !== r.height
        ) {
          didChange = true;
        }

        nextWindowsById[windowId] = sanitizedRect;
        return nextWindowsById;
      },
      {}
    );

    if (Object.keys(sanitizedWindowsById).length === 0) {
      didChange = true;
      return acc;
    }

    acc[pageType as PageTypes] = sanitizedWindowsById;
    return acc;
  }, {});

  return { nextState, didChange };
};

export const useWindowsRects = (): [WindowsState, Dispatch<SetStateAction<WindowsState>>] => {
  // @ts-expect-error [EN-7967] - TS2322 - Type 'unknown[]' is not assignable to type '[Partial<Record<PageTypes, { [key: string]: WindowRect; }>>, Dispatch<SetStateAction<Partial<Record<PageTypes, { [key: string]: WindowRect; }>>>>]'.
  const [windowsState, setWindowsState] = useLocalStorage('windows-state', {});

  useEffect(() => {
    const { nextState, didChange } = sanitizeWindowsState(windowsState);
    if (!didChange) return;

    setWindowsState(() => nextState);
  }, [setWindowsState, windowsState]);

  return [windowsState, setWindowsState];
};

const getWindowIdWithDefault = (url: string) => getWindowId(url) ?? 'default';

export const useWindowState = (): [WindowRect, Dispatch<WindowRect>] => {
  const location = useLocation();
  const url = `${window.origin}${location.pathname}`;
  const pageType = getPageType(location.pathname);
  const windowId = getWindowIdWithDefault(url);

  const [windowsState, setWindowsState] = useWindowsRects();

  const setWindowState = useCallback(
    ({ top, left, width, height }: WindowRect) => {
      const nextWindowRect = sanitizeWindowRect({
        rect: { top, left, width, height },
      });
      if (nextWindowRect == null) return;

      setWindowsState((windowsState) => ({
        ...windowsState,
        [String(pageType)]: {
          ...windowsState[pageType],
          [String(windowId)]: nextWindowRect,
        },
      }));
    },
    [pageType, setWindowsState, windowId]
  );

  // @ts-expect-error [EN-10263] - TS2322 - Type 'WindowRect | undefined' is not assignable to type 'WindowRect'.
  return [windowsState?.[pageType]?.[windowId], setWindowState];
};

// Tracks the window state for the current page type and window id
export const useTrackWindowRect = (): void => {
  const [, setWindowState] = useWindowState();
  const screenPosition = useRef<{
    screenY: Nullable<number>;
    screenX: Nullable<number>;
  }>({
    screenY: null,
    screenX: null,
  });

  useEffect(() => {
    // Used to track the window size and position when user is resizing it
    const resizeHandler = () => {
      const { innerWidth, innerHeight, screenX, screenY } = window;
      setWindowState({ width: innerWidth, height: innerHeight, top: screenY, left: screenX });
    };
    window.addEventListener('resize', resizeHandler);

    // The following code is necessary to track the position of the browser window
    const intervalId = setInterval(() => {
      if (
        screenPosition.current.screenY !== window.screenY ||
        screenPosition.current.screenX !== window.screenX
      ) {
        screenPosition.current.screenY = window.screenY as number;
        screenPosition.current.screenX = window.screenX as number;
        resizeHandler();
      }
    }, 5000);

    return () => {
      window.removeEventListener('resize', resizeHandler);
      clearInterval(intervalId);
    };
  }, [setWindowState]);
};

export type PopupOptionsGetter = (arg1: string) => WindowRect;

export const usePopupOptionsGetter = (): PopupOptionsGetter => {
  const [windowsState] = useWindowsRects();
  const getPopupOptions = useCallback(
    (url: string) => {
      const pageType = getPageType(url);
      const popupOptions = windowsState?.[pageType]?.[getWindowIdWithDefault(url)];
      const sanitizedPopupOptions = sanitizeWindowRect({ rect: popupOptions });
      return sanitizedPopupOptions == null ? undefined : sanitizedPopupOptions;
    },
    [windowsState]
  );

  // @ts-expect-error [EN-10263] - TS2322 - Type '(url: string) => WindowRect | undefined' is not assignable to type 'PopupOptionsGetter'.
  return getPopupOptions;
};

/**
 * Re-sanitize saved window rects on display topology changes.
 * Handles cases where monitors are disconnected and stale extreme coordinates
 * persist in localStorage. Triggers on focus/pageshow/visualViewport resize.
 */
export const useWindowRectsSanitizer = (): void => {
  const [, setWindowsState] = useWindowsRects();

  useEffect(() => {
    let timeoutHandle: ReturnType<typeof setTimeout>;

    const sanitizeWindowState = () => {
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
      }

      timeoutHandle = setTimeout(() => {
        setWindowsState((prev) => {
          const { nextState, didChange } = sanitizeWindowsState(prev);
          return didChange ? nextState : prev;
        });
      }, 500);
    };

    window.addEventListener('focus', sanitizeWindowState);
    window.addEventListener('pageshow', sanitizeWindowState);
    window.visualViewport?.addEventListener('resize', sanitizeWindowState);

    return () => {
      clearTimeout(timeoutHandle);
      window.removeEventListener('focus', sanitizeWindowState);
      window.removeEventListener('pageshow', sanitizeWindowState);
      window.visualViewport?.removeEventListener('resize', sanitizeWindowState);
    };
  }, [setWindowsState]);
};
